import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Paperclip, Smile, ThumbsUp, ChevronDown, Sparkles } from 'lucide-react';
import { api } from '../lib/api';

// Quick-reply chips, mirroring the screenshot reference
const QUICK_REPLIES = [
  { id: 'land', label: 'Land Surveying / Development CAD Drafting' },
  { id: 'bim', label: 'BIM Services | 3D Modeling | Architectural | Steel Detailing' },
  { id: 'civil-mech', label: 'Civil & Mechanical Engineering | Digitalization' },
  { id: 'it', label: 'IT Solutions | Technology' },
  { id: 'quote', label: 'I want a quote' },
  { id: 'general', label: 'Other / General question' }
];

const greet = "👋 Hi! How can we help?\nNeed Assistance? CAD | IT Solutions | Technology";

const sessionKey = () => {
  try {
    let s = sessionStorage.getItem('ld_chat_session');
    if (!s) {
      s = 'chat-' + Math.random().toString(36).slice(2, 12) + '-' + Date.now().toString(36);
      sessionStorage.setItem('ld_chat_session', s);
    }
    return s;
  } catch { return 'chat-anon-' + Date.now(); }
};

const SupportChat = () => {
  const [open, setOpen] = useState(false);
  const [unseen, setUnseen] = useState(true);
  const [messages, setMessages] = useState([
    { id: 'm0', sender: 'bot', text: greet }
  ]);
  const [draft, setDraft] = useState('');
  const [showQuick, setShowQuick] = useState(true);
  const [collectingEmail, setCollectingEmail] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const listRef = useRef(null);
  const session = useRef(sessionKey()).current;

  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) setUnseen(false);
  }, [open]);

  const persist = (msg) => api.chat({
    session_id: session,
    sender: msg.sender,
    text: msg.text,
    quick_reply: msg.quick_reply || '',
    email: userEmail || ''
  });

  const pushBot = (text, delay = 600) => {
    setTimeout(() => {
      setMessages(prev => [...prev, { id: 'b-' + Date.now(), sender: 'bot', text }]);
      if (!open) setUnseen(true);
    }, delay);
  };

  const [llmThinking, setLlmThinking] = useState(false);

  const sendUser = async (text, quickReplyId = '') => {
    if (!text || !text.trim()) return;
    const userMsg = { id: 'u-' + Date.now(), sender: 'user', text: text.trim(), quick_reply: quickReplyId };
    setMessages(prev => [...prev, userMsg]);
    setDraft('');
    setShowQuick(false);

    // Capture an email if one is typed
    if (collectingEmail && /\S+@\S+\.\S+/.test(text)) {
      setUserEmail(text.trim());
      setCollectingEmail(false);
    }

    // Ask the LLM for a reply via the chat API (it stores both turns and returns the bot reply)
    setLlmThinking(true);
    try {
      const res = await persist(userMsg);
      const replyText = res?.reply?.text;
      if (replyText) {
        setMessages(prev => [...prev, { id: 'b-' + (res?.reply?.id || Date.now()), sender: 'bot', text: replyText }]);
        if (!open) setUnseen(true);
      } else {
        // Fallback if API didn't return a reply (LLM not configured, network error)
        pushBot('Thanks! A team member will reply shortly. Meanwhile feel free to share more details, or reach us at info@landdrafters.com.');
      }
    } catch {
      pushBot('I had a brief connectivity issue — please try again, or reach us at info@landdrafters.com.');
    } finally {
      setLlmThinking(false);
    }

    // After quick-reply categories, gently ask for email if we don't have one yet
    if (quickReplyId && quickReplyId !== 'general' && !userEmail) {
      setTimeout(() => setCollectingEmail(true), 1400);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendUser(draft);
  };

  return (
    <>
      {/* Floating launcher */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open customer support chat"
        data-testid="chat-launcher"
        className={`fixed bottom-6 right-6 z-[9970] w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 ${open ? 'scale-0 opacity-0' : 'scale-100 opacity-100'} bg-orange-500 hover:bg-orange-600 text-white ring-4 ring-orange-400/30 hover:ring-orange-300/50`}
      >
        <MessageCircle className="w-6 h-6" />
        {unseen && !open && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-orange-500 rounded-full border-2 border-white animate-pulse"></span>}
      </button>

      {/* Chat panel */}
      <div
        data-testid="chat-panel"
        className={`fixed bottom-6 right-6 z-[9970] w-[min(96vw,360px)] h-[min(80vh,560px)] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col transition-all duration-300 origin-bottom-right ${open ? 'opacity-100 scale-100' : 'opacity-0 scale-0 pointer-events-none'}`}
        role="dialog"
        aria-label="Customer support chat"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-gray-700 via-gray-600 to-orange-500 text-white px-5 py-4 flex items-center justify-between relative">
          <div className="absolute inset-0 opacity-20 [background-image:linear-gradient(rgba(255,255,255,0.15)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.15)_1px,transparent_1px)] [background-size:18px_18px] pointer-events-none"></div>
          <div className="relative flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold text-sm flex items-center gap-1.5">Customer Support <Sparkles className="w-3 h-3" /></div>
              <div className="text-[11px] opacity-90 flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-pulse"></span> Typically replies in minutes
              </div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Close chat" data-testid="chat-close" className="relative w-8 h-8 rounded-full hover:bg-white/15 flex items-center justify-center transition-colors">
            <ChevronDown className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div ref={listRef} className="flex-1 overflow-y-auto bg-gradient-to-b from-amber-50 via-white to-gray-50 px-4 py-4 space-y-3" data-testid="chat-messages">
          <div className="text-center text-[10px] uppercase tracking-wider text-gray-400 font-bold">Customer Support</div>
          {messages.map(m => (
            <div key={m.id} className={`flex items-end gap-2 ${m.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.sender === 'bot' && (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-500 to-orange-500 flex items-center justify-center text-white text-[10px] font-black shrink-0">LD</div>
              )}
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap shadow-sm ${m.sender === 'user' ? 'bg-gray-600 text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'}`}>
                {m.text}
              </div>
            </div>
          ))}

          {/* Typing indicator while the LLM is thinking */}
          {llmThinking && (
            <div className="flex items-end gap-2 justify-start" data-testid="chat-typing">
              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-gray-500 to-orange-500 flex items-center justify-center text-white text-[10px] font-black shrink-0">LD</div>
              <div className="bg-white text-gray-500 border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm flex items-center gap-1">
                <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                <span className="w-1.5 h-1.5 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
              </div>
            </div>
          )}

          {/* Quick replies */}
          {showQuick && (
            <div className="pt-2 space-y-2" data-testid="chat-quick-replies">
              {QUICK_REPLIES.map(q => (
                <button
                  key={q.id}
                  type="button"
                  onClick={() => sendUser(q.label, q.id)}
                  data-testid={`chat-quick-${q.id}`}
                  className="w-full text-left bg-white border border-gray-200 hover:border-green-500 hover:bg-gray-50 text-gray-700 hover:text-green-700 text-[12px] font-semibold px-3 py-2.5 rounded-lg transition-all hover:translate-x-1"
                >
                  {q.label}
                </button>
              ))}
            </div>
          )}

          {collectingEmail && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-[12px] text-orange-700">
              ✉️ Please type your email address so we can follow up.
            </div>
          )}
        </div>

        {/* Composer */}
        <form onSubmit={onSubmit} className="border-t border-gray-200 bg-white px-3 py-2.5 flex items-center gap-2" data-testid="chat-composer">
          <button type="button" aria-label="Attach" className="text-gray-400 hover:text-green-600 p-1"><Paperclip className="w-4 h-4" /></button>
          <input
            type="text"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Type here and press enter…"
            data-testid="chat-input"
            className="flex-1 bg-transparent border-0 focus:outline-none text-sm placeholder:text-gray-400"
          />
          <button type="button" aria-label="Emoji" className="text-gray-400 hover:text-orange-500 p-1"><Smile className="w-4 h-4" /></button>
          <button type="button" aria-label="Thumbs up" onClick={() => sendUser('👍')} className="text-gray-400 hover:text-green-600 p-1"><ThumbsUp className="w-4 h-4" /></button>
          <button type="submit" aria-label="Send" data-testid="chat-send" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full w-9 h-9 flex items-center justify-center transition-colors">
            <Send className="w-4 h-4" />
          </button>
        </form>

        {/* Footer brand */}
        <div className="text-center text-[10px] text-gray-400 py-1.5 border-t border-gray-100 bg-white">
          Powered by <span className="font-bold text-green-600">Land Drafters</span>
        </div>
      </div>
    </>
  );
};

export default SupportChat;
